"use client";
import { AppFromLabelField, AppSelect } from "@/common";
import { AppConstant } from "@/const";
import { VALUE_SELECT_ALL } from "@/const/app.const";
import { AppOptionType } from "@/models";
import {
  BoxProps,
  FormControl,
  FormHelperText,
  InputLabelProps,
  SelectChangeEvent,
  SelectProps,
} from "@mui/material";
import React, { ReactNode, memo } from "react";
import {
  Control,
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
  RegisterOptions,
} from "react-hook-form";

const AppFormControlSelect = <T extends FieldValues>({
  label,
  control,
  name,
  rules,
  options,
  isHasAllOption,
  controlProps,
  labelProps,
  selectProps,
  onChangeValueForm,
  classNameContainer,
  isLoading,
  helper,
  childrenWrapperProps,
}: AppFormControlSelectProps<T>) => {
  return (
    <AppFromLabelField
      label={label}
      labelProps={{
        ...labelProps,
      }}
      className={classNameContainer}
      required={labelProps?.required}
      helper={helper}
      childrenWrapperProps={childrenWrapperProps}
    >
      <>
        <Controller
          control={control}
          name={name}
          rules={rules}
          render={({
            field: { onChange, value, ...otherFieldProps },
            fieldState: { error: errorField },
          }) => (
            <FormControl
              fullWidth
              error={!!errorField}
              margin="normal"
              sx={{
                margin: 0,
              }}
            >
              <AppSelect
                isLoading={isLoading}
                value={
                  value === AppConstant.BooleanTypeEnum.False || value === false
                    ? value
                    : !isHasAllOption
                      ? value || ""
                      : value === "" || value === undefined || value === null
                        ? VALUE_SELECT_ALL
                        : value
                }
                onChange={(
                  event: SelectChangeEvent<unknown>,
                  child: React.ReactNode,
                ) => {
                  onChange(event, child);
                  if (onChangeValueForm instanceof Function)
                    onChangeValueForm(event);
                }}
                options={
                  isHasAllOption
                    ? [
                        {
                          label: AppConstant.ALL_OPTIONS.label ?? "",
                          value: AppConstant.ALL_OPTIONS.value,
                        },
                        ...options,
                      ]
                    : options
                }
                {...otherFieldProps}
                {...selectProps}
              />
              {errorField && (
                <FormHelperText>{errorField?.message}</FormHelperText>
              )}
            </FormControl>
          )}
          {...controlProps}
        />
      </>
    </AppFromLabelField>
  );
};

type AppFormControlSelectProps<T extends FieldValues> = {
  isLoading?: boolean;
  label?: string | ReactNode;
  control: Control<any>;
  name: FieldPath<T>;
  options: AppOptionType[];
  isHasAllOption?: boolean;
  rules?: RegisterOptions;
  controlProps?: Omit<ControllerProps, "render" | "name" | "control">;
  labelProps?: InputLabelProps;
  selectProps?: SelectProps;
  onChangeValueForm?: (event?: SelectChangeEvent<unknown>) => void;
  classNameContainer?: string;
  helper?: string | ReactNode;
  childrenWrapperProps?: BoxProps;
};

export default memo(AppFormControlSelect);
